if (!customElements.get('kraven-scroll-canvas-v5')) {
  customElements.define(
    'kraven-scroll-canvas-v5',
    class extends HTMLElement {
      connectedCallback() {
        this.canvas = this.querySelector('[data-kraven-sequence-canvas]')
        this.stage = this.querySelector('.kraven-scroll-hero__stage')
        this.cue = this.querySelector('.kraven-scroll-hero__cue')
        this.mobileCropQuery = window.matchMedia('(max-width: 749px)')
        const navigatorPlatform = navigator.userAgentData?.platform || navigator.platform || ''
        this.isIOSWebKit =
          /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
          (navigatorPlatform === 'MacIntel' && navigator.maxTouchPoints > 1)
        if (this.isIOSWebKit) this.setAttribute('data-kraven-ios', 'true')
        this.iosAtlasTemplate = this.dataset.iosAtlasTemplate || ''
        this.iosAtlasSize = Number.parseInt(this.dataset.iosAtlasSize || '10', 10)
        this.iosAtlasColumns = Number.parseInt(this.dataset.iosAtlasColumns || '2', 10)
        this.useIOSAtlas = Boolean(
          this.isIOSWebKit &&
            this.mobileCropQuery.matches &&
            this.iosAtlasTemplate &&
            this.iosAtlasSize > 0 &&
            this.iosAtlasColumns > 0
        )
        this.frameTemplate =
          this.mobileCropQuery.matches && this.isIOSWebKit && this.dataset.iosFrameTemplate
            ? this.dataset.iosFrameTemplate
            : this.mobileCropQuery.matches && this.dataset.mobileFrameTemplate
            ? this.dataset.mobileFrameTemplate
            : this.dataset.frameTemplate
        this.frameCount = Number.parseInt(this.dataset.frameCount || '72', 10)
        if (!this.canvas || !this.stage || !this.frameTemplate || !this.frameCount) return

        this.urls = Array.from({ length: this.frameCount }, (_, index) =>
          this.frameTemplate.replace('{frame}', String(index))
        )
        this.iosAtlasRows = Math.ceil(this.iosAtlasSize / this.iosAtlasColumns)
        this.iosAtlasUrls = this.useIOSAtlas
          ? Array.from({ length: Math.ceil(this.frameCount / this.iosAtlasSize) }, (_, index) =>
              this.iosAtlasTemplate.replace('{atlas}', String(index))
            )
          : []
        this.context = this.canvas.getContext('2d', {
          alpha: true,
          desynchronized: this.isIOSWebKit,
        })
        if (!this.context) return
        this.context.imageSmoothingEnabled = true
        this.context.imageSmoothingQuality = this.isIOSWebKit ? 'medium' : 'high'

        this.images = new Map()
        this.frame = 0
        this.isNearViewport = true
        this.targetIndex = 0
        this.renderedIndex = -1
        this.anchorIndexes = new Set([0, this.urls.length - 1])
        this.cacheWarmTimer = 0
        this.cacheWarmIdle = 0
        this.mobileReleaseTimer = 0
        this.mobileWarmResumeTimer = 0
        this.mobileWarmGeneration = 0
        this.mobileFramesReleased = false
        this.hasTouchScrubStarted = false
        this.resizeFrame = 0
        this.orientationTimer = 0
        this.iosWarmTimer = 0
        this.iosWarmIdle = 0
        this.iosWarmGeneration = 0
        this.iosFrameQueue = []
        this.iosLoadsInFlight = 0
        this.iosAtlases = new Map()
        this.iosAtlasWarmTimer = 0
        this.iosAtlasWarmIdle = 0
        this.iosOpeningResetTimers = []
        this.iosOpeningGuardUntil = performance.now() + 1000
        this.iosVisualPosition = 0
        this.iosDesiredPosition = 0
        this.iosGestureFramePosition = 0
        this.iosStableScrollTop = null
        this.iosScrollDirection = 0
        this.iosReverseThreshold = 10
        this.iosLastDrawnPosition = null
        this.iosLastProgress = -1
        this.iosCompositeLayers = []
        this.iosCompositeGeometryVersion = 0
        this.iosCompositeStageWidth = 0
        this.iosCompositeStageHeight = 0
        this.iosStageState = ''
        this.iosLastSampledScrollTop = 0
        this.isIOSTouching = false
        this.iosMomentumUntil = 0
        this.lastViewportWidth = Math.round(window.visualViewport?.width || window.innerWidth || 0)
        this.mobileAnchorIndexes = []
        this.remainingWarmIndexes = []
        this.warmedFrameIndexes = new Set()
        this.cacheAbortController = new AbortController()
        this.schedule = this.schedule.bind(this)
        this.update = this.update.bind(this)
        this.resize = this.resize.bind(this)
        this.handleViewportResize = this.handleViewportResize.bind(this)
        this.handleOrientationChange = this.handleOrientationChange.bind(this)
        this.handleTouchStart = this.handleTouchStart.bind(this)
        this.handleTouchEnd = this.handleTouchEnd.bind(this)
        this.handlePageShow = this.handlePageShow.bind(this)
        this.bindScrollContainer = this.bindScrollContainer.bind(this)
        this.squeezeQuery = window.matchMedia('(min-width: 990px)')

        this.bindScrollContainer()
        if ('IntersectionObserver' in window) {
          this.viewportObserver = new IntersectionObserver(
            ([entry]) => {
              this.isNearViewport = entry.isIntersecting
              if (this.isNearViewport) this.schedule()
            },
            { rootMargin: '400px 0px' }
          )
          this.viewportObserver.observe(this)
        }
        this.squeezeQuery.addEventListener('change', this.bindScrollContainer)
        window.addEventListener('resize', this.handleViewportResize, { passive: true })
        window.addEventListener('orientationchange', this.handleOrientationChange, { passive: true })
        window.addEventListener('pageshow', this.handlePageShow, { passive: true })
        this.stage.addEventListener('touchstart', this.handleTouchStart, { passive: true })
        window.addEventListener('touchend', this.handleTouchEnd, { passive: true })
        window.addEventListener('touchcancel', this.handleTouchEnd, { passive: true })
        document.body.classList.add('kraven-cinematic-header-active')

        this.resize()
        if (this.useIOSAtlas) {
          this.dataset.kravenSequenceMode = 'ios-atlas'
          this.setupIOSCompositeLayers()
          this.loadIOSAtlas(0, true).then(() => {
            if (this.isConnected && !this.hasTouchScrubStarted) this.drawIOSAtlasPosition(0)
          })
          this.loadIOSAtlas(1)
          this.scheduleIOSAtlasWarmup()
          this.scheduleIOSOpeningReset()
        } else {
          this.loadFrame(0, true)
          this.preloadWindow(0)
          this.scheduleInitialIOSWarmup()
          this.warmFrameCache()
        }
      }

      disconnectedCallback() {
        this.viewportObserver?.disconnect()
        this.scrollTarget?.removeEventListener('scroll', this.schedule)
        this.squeezeQuery?.removeEventListener('change', this.bindScrollContainer)
        window.removeEventListener('resize', this.handleViewportResize)
        window.removeEventListener('orientationchange', this.handleOrientationChange)
        window.removeEventListener('pageshow', this.handlePageShow)
        this.stage?.removeEventListener('touchstart', this.handleTouchStart)
        window.removeEventListener('touchend', this.handleTouchEnd)
        window.removeEventListener('touchcancel', this.handleTouchEnd)
        if (this.frame) cancelAnimationFrame(this.frame)
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame)
        if (this.cacheWarmTimer) window.clearTimeout(this.cacheWarmTimer)
        if (this.mobileReleaseTimer) window.clearTimeout(this.mobileReleaseTimer)
        if (this.mobileWarmResumeTimer) window.clearTimeout(this.mobileWarmResumeTimer)
        if (this.orientationTimer) window.clearTimeout(this.orientationTimer)
        if (this.iosWarmTimer) window.clearTimeout(this.iosWarmTimer)
        if (this.iosAtlasWarmTimer) window.clearTimeout(this.iosAtlasWarmTimer)
        this.iosOpeningResetTimers.forEach((timer) => window.clearTimeout(timer))
        if (this.cacheWarmIdle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.cacheWarmIdle)
        }
        if (this.iosWarmIdle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.iosWarmIdle)
        }
        if (this.iosAtlasWarmIdle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.iosAtlasWarmIdle)
        }
        this.cacheAbortController?.abort()
        this.images?.forEach((entry) => {
          if (entry.image) {
            entry.image.onload = null
            entry.image.onerror = null
          }
          entry.resolve?.(null)
        })
        this.iosFrameQueue = []
        this.images?.clear()
        this.iosAtlases?.forEach((entry) => {
          if (entry.image) {
            entry.image.onload = null
            entry.image.onerror = null
          }
          entry.resolve?.(null)
        })
        this.iosAtlases?.clear()
        this.iosCompositeLayers.forEach((slot) => slot.layer.remove())
        this.iosCompositeLayers = []
        document.body.classList.remove('kraven-cinematic-header-active')
      }

      handleTouchStart() {
        this.hasTouchScrubStarted = true
        this.isIOSTouching = this.useIOSAtlas
        this.iosScrollDirection = 0
        this.iosStableScrollTop = this.scrollContainer?.scrollTop || 0
        this.iosGestureFramePosition = this.iosVisualPosition
        this.iosLastSampledScrollTop = this.scrollContainer?.scrollTop || 0
        this.iosOpeningGuardUntil = 0
        this.iosOpeningResetTimers.forEach((timer) => window.clearTimeout(timer))
        this.iosOpeningResetTimers = []
        this.iosWarmGeneration += 1
        this.dataset.kravenInteracted = 'true'
        this.schedule()
      }

      handleTouchEnd() {
        if (!this.useIOSAtlas) return
        this.isIOSTouching = false
        this.iosMomentumUntil = performance.now() + 320
        this.schedule()
      }

      stabilizeIOSScrollTop(scrollTop) {
        if (!this.useIOSAtlas || !this.hasTouchScrubStarted) {
          this.iosStableScrollTop = scrollTop
          return scrollTop
        }

        if (!Number.isFinite(this.iosStableScrollTop)) this.iosStableScrollTop = scrollTop
        const stable = this.iosStableScrollTop

        if (this.iosScrollDirection === 0) {
          const movement = scrollTop - stable
          if (Math.abs(movement) >= 0.75) {
            this.iosScrollDirection = Math.sign(movement)
            this.iosStableScrollTop = scrollTop
          }
          return this.iosStableScrollTop
        }

        if (this.iosScrollDirection > 0) {
          if (scrollTop >= stable) {
            this.iosStableScrollTop = scrollTop
          } else if (stable - scrollTop >= this.iosReverseThreshold) {
            this.iosScrollDirection = -1
            this.iosStableScrollTop = scrollTop
          }
          return this.iosStableScrollTop
        }

        if (scrollTop <= stable) {
          this.iosStableScrollTop = scrollTop
        } else if (scrollTop - stable >= this.iosReverseThreshold) {
          this.iosScrollDirection = 1
          this.iosStableScrollTop = scrollTop
        }
        return this.iosStableScrollTop
      }

      handlePageShow() {
        if (this.useIOSAtlas) {
          this.iosOpeningGuardUntil = performance.now() + 900
          this.scheduleIOSOpeningReset()
        }
        window.setTimeout(() => {
          if (!this.isConnected || !this.isAtSequenceStart(24)) return
          this.targetIndex = 0
          this.iosDesiredPosition = 0
          this.iosVisualPosition = 0
          this.iosGestureFramePosition = 0
          this.iosStableScrollTop = 0
          this.iosScrollDirection = 0
          this.iosLastDrawnPosition = null
          if (this.useIOSAtlas) {
            this.loadIOSAtlas(0, true).then(() => {
              if (this.isConnected && this.isAtSequenceStart(24)) this.drawIOSAtlasPosition(0)
            })
            return
          }
          this.loadFrame(0, true).then(() => {
            if (this.isConnected && this.isAtSequenceStart(24)) this.drawFrame(0)
          })
        }, 0)
      }

      isIOSOpeningViewport() {
        if (!this.useIOSAtlas || !this.stage) return false
        const rect = this.getBoundingClientRect()
        const stageHeight = Math.max(this.stage.offsetHeight, 1)
        return rect.top < stageHeight * 0.3 && rect.bottom > stageHeight * 0.72
      }

      scheduleIOSOpeningReset() {
        if (!this.useIOSAtlas || this.hasTouchScrubStarted || location.hash) return
        this.iosOpeningResetTimers.forEach((timer) => window.clearTimeout(timer))
        this.iosOpeningResetTimers = [0, 80, 240, 520, 850].map((delay) =>
          window.setTimeout(() => this.resetIOSOpeningPosition(), delay)
        )
      }

      resetIOSOpeningPosition() {
        if (
          !this.isConnected ||
          !this.useIOSAtlas ||
          this.hasTouchScrubStarted ||
          !this.isIOSOpeningViewport()
        ) {
          return
        }

        const scrollTop = this.scrollContainer?.scrollTop || 0
        const distance = Math.max(this.offsetHeight - this.stage.offsetHeight, 1)
        if (scrollTop > 1 && scrollTop <= distance + 4) {
          const root = document.documentElement
          const previousBehavior = root.style.scrollBehavior
          root.style.scrollBehavior = 'auto'
          this.scrollContainer.scrollTop = 0
          document.body.scrollTop = 0
          root.scrollTop = 0
          root.style.scrollBehavior = previousBehavior
        }

        this.targetIndex = 0
        this.iosDesiredPosition = 0
        this.iosVisualPosition = 0
        this.iosGestureFramePosition = 0
        this.iosStableScrollTop = 0
        this.iosScrollDirection = 0
        this.iosLastDrawnPosition = null
        this.dataset.kravenProgress = '0.0000'
        this.dataset.kravenFrame = '0'
        this.loadIOSAtlas(0, true).then(() => {
          if (this.isConnected && !this.hasTouchScrubStarted && this.isIOSOpeningViewport()) {
            this.drawIOSAtlasPosition(0)
          }
        })
      }

      isAtSequenceStart(tolerance = 4) {
        const scrollTop = this.scrollContainer?.scrollTop || 0
        const containerTop =
          this.squeezeQuery.matches && this.scrollContainer instanceof Element
            ? this.scrollContainer.getBoundingClientRect().top
            : 0
        const rootTop = scrollTop + this.getBoundingClientRect().top - containerTop
        const sequenceStart = Math.max(rootTop, 0)
        return scrollTop - sequenceStart <= tolerance
      }

      handleViewportResize() {
        const viewportWidth = Math.round(window.visualViewport?.width || window.innerWidth || 0)

        // iOS fires height-only resize events continuously as its address bar
        // expands and collapses. Reallocating a full-screen canvas for those
        // events is the main source of touch-scroll judder.
        if (
          this.isIOSWebKit &&
          this.mobileCropQuery.matches &&
          this.lastViewportWidth &&
          Math.abs(viewportWidth - this.lastViewportWidth) < 2
        ) {
          return
        }

        this.lastViewportWidth = viewportWidth
        if (this.useIOSAtlas) {
          this.style.removeProperty('--kraven-ios-stage-height')
          this.style.removeProperty('--kraven-ios-root-height')
        }
        if (this.resizeFrame) return
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = 0
          this.resize()
        })
      }

      handleOrientationChange() {
        this.lastViewportWidth = 0
        if (this.useIOSAtlas) {
          this.style.removeProperty('--kraven-ios-stage-height')
          this.style.removeProperty('--kraven-ios-root-height')
        }
        if (this.orientationTimer) window.clearTimeout(this.orientationTimer)
        this.orientationTimer = window.setTimeout(() => {
          this.orientationTimer = 0
          this.lastViewportWidth = Math.round(window.visualViewport?.width || window.innerWidth || 0)
          this.resize()
        }, 220)
      }

      loadIOSAtlas(index, urgent = false) {
        if (!this.useIOSAtlas || index < 0 || index >= this.iosAtlasUrls.length) {
          return Promise.resolve(null)
        }

        const existing = this.iosAtlases.get(index)
        if (existing) return existing.promise

        const entry = { image: null, loaded: false, promise: null, resolve: null }
        entry.promise = new Promise((resolve) => {
          entry.resolve = resolve
        })
        this.iosAtlases.set(index, entry)

        const image = new Image()
        image.decoding = 'async'
        image.fetchPriority = urgent ? 'high' : 'low'
        entry.image = image

        image.onload = async () => {
          try {
            await image.decode()
          } catch (error) {
            this.dataset.kravenSequenceDecode = 'atlas-fallback'
          }
          entry.loaded = true
          entry.resolve?.(image)
          entry.resolve = null
          if (index === Math.floor(this.iosDesiredPosition / this.iosAtlasSize)) this.schedule()
        }

        image.onerror = () => {
          if (this.iosAtlases.get(index) === entry) this.iosAtlases.delete(index)
          entry.resolve?.(null)
          entry.resolve = null
        }
        image.src = this.iosAtlasUrls[index]
        return entry.promise
      }

      setupIOSCompositeLayers() {
        if (!this.useIOSAtlas || this.iosCompositeLayers.length) return
        const media = this.querySelector('.kraven-scroll-hero__media')
        if (!media) return

        this.iosCompositeLayers = [0, 1].map((index) => {
          const layer = document.createElement('span')
          const image = document.createElement('img')
          layer.className = `kraven-scroll-hero__ios-frame kraven-scroll-hero__ios-frame--${index}`
          layer.setAttribute('aria-hidden', 'true')
          image.alt = ''
          image.decoding = 'async'
          image.draggable = false
          layer.append(image)
          media.append(layer)
          return { layer, image, frameIndex: -1, geometryVersion: -1 }
        })
      }

      scheduleIOSAtlasWarmup() {
        if (!this.useIOSAtlas) return
        const warm = async () => {
          for (let index = 2; index < this.iosAtlasUrls.length; index += 1) {
            if (!this.isConnected) return
            await this.loadIOSAtlas(index)
          }
          if (this.isConnected) this.dataset.kravenSequenceIOS = 'atlas-primed'
        }

        if ('requestIdleCallback' in window) {
          this.iosAtlasWarmIdle = window.requestIdleCallback(warm, { timeout: 700 })
        } else {
          this.iosAtlasWarmTimer = window.setTimeout(warm, 100)
        }
      }

      preloadIOSAtlasWindow(framePosition) {
        const currentAtlas = Math.floor(framePosition / this.iosAtlasSize)
        this.loadIOSAtlas(currentAtlas, true)
        this.loadIOSAtlas(currentAtlas + 1)
        this.loadIOSAtlas(currentAtlas - 1)
      }

      scheduleInitialIOSWarmup() {
        if (!this.isIOSWebKit || !this.mobileCropQuery.matches) return
        const warm = () => this.warmInitialIOSFrames()

        if ('requestIdleCallback' in window) {
          this.iosWarmIdle = window.requestIdleCallback(warm, { timeout: 500 })
        } else {
          this.iosWarmTimer = window.setTimeout(warm, 80)
        }
      }

      async warmInitialIOSFrames() {
        const generation = this.iosWarmGeneration
        for (let index = 5; index <= Math.min(10, this.urls.length - 1); index += 1) {
          if (
            !this.isConnected ||
            this.hasTouchScrubStarted ||
            generation !== this.iosWarmGeneration ||
            !this.isAtSequenceStart(24)
          ) {
            return
          }
          await this.loadFrame(index)
        }
        this.dataset.kravenSequenceIOS = 'primed'
      }

      bindScrollContainer() {
        this.scrollTarget?.removeEventListener('scroll', this.schedule)
        const documentScroller = document.scrollingElement || document.documentElement

        if (this.squeezeQuery.matches) {
          this.scrollContainer = document.querySelector('.page-wrapper') || documentScroller
          this.scrollTarget = this.scrollContainer
        } else {
          this.scrollContainer = documentScroller
          // Window scroll events are delivered more consistently than document
          // scroll events during an active WKWebView touch gesture (Safari and
          // every iOS browser, including Chrome).
          this.scrollTarget = this.isIOSWebKit ? window : document
        }

        this.scrollTarget.addEventListener('scroll', this.schedule, { passive: true })
        this.schedule()
      }

      loadFrame(index, urgent = false) {
        const boundedIndex = Math.min(Math.max(index, 0), this.urls.length - 1)
        const existing = this.images.get(boundedIndex)
        if (existing) return existing.promise

        if (this.isIOSWebKit && this.mobileCropQuery.matches) {
          return this.queueIOSFrame(boundedIndex, urgent)
        }

        const entry = { image: null, loaded: false, promise: null, resolve: null }
        entry.promise = new Promise((resolve) => {
          entry.resolve = resolve
        })
        this.images.set(boundedIndex, entry)
        this.beginFrameLoad(boundedIndex, urgent, entry)
        return entry.promise
      }

      queueIOSFrame(index, urgent) {
        const entry = { image: null, loaded: false, promise: null, resolve: null }
        entry.promise = new Promise((resolve) => {
          entry.resolve = resolve
        })
        this.images.set(index, entry)

        if (urgent) {
          this.iosFrameQueue = this.iosFrameQueue.filter((job) => {
            const keep = Math.abs(job.index - index) <= 6 || job.index === 0 || job.index === this.urls.length - 1
            if (!keep) {
              if (this.images.get(job.index) === job.entry) this.images.delete(job.index)
              job.entry.resolve?.(null)
            }
            return keep
          })
        }

        const job = { index, urgent, entry }
        if (urgent) this.iosFrameQueue.unshift(job)
        else this.iosFrameQueue.push(job)
        this.pumpIOSFrameQueue()
        return entry.promise
      }

      pumpIOSFrameQueue() {
        while (this.iosLoadsInFlight < 2 && this.iosFrameQueue.length && this.isConnected) {
          const job = this.iosFrameQueue.shift()
          if (this.images.get(job.index) !== job.entry) {
            job.entry.resolve?.(null)
            continue
          }

          this.iosLoadsInFlight += 1
          this.beginFrameLoad(job.index, job.urgent, job.entry, () => {
            this.iosLoadsInFlight = Math.max(this.iosLoadsInFlight - 1, 0)
            this.pumpIOSFrameQueue()
          })
        }
      }

      beginFrameLoad(boundedIndex, urgent, entry, onSettled = () => {}) {
        const image = new Image()
        image.decoding = 'async'
        image.fetchPriority = urgent ? 'high' : 'auto'
        entry.image = image

        image.onload = async () => {
          try {
            await image.decode()
          } catch (error) {
            this.dataset.kravenSequenceDecode = 'fallback'
          }
          entry.loaded = true
          if (boundedIndex === this.targetIndex || (this.renderedIndex < 0 && boundedIndex === 0)) {
            this.drawFrame(boundedIndex)
          }
          entry.resolve?.(image)
          entry.resolve = null
          onSettled()
        }

        image.onerror = () => {
          if (this.images.get(boundedIndex) === entry) this.images.delete(boundedIndex)
          entry.resolve?.(null)
          entry.resolve = null
          onSettled()
        }
        image.src = this.urls[boundedIndex]
      }

      async warmFrameCache() {
        if (!('fetch' in window)) return

        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
        const isSlowConnection = /(^|-)2g$/.test(connection?.effectiveType || '')
        const saveData = Boolean(connection?.saveData)
        // Keep evenly spaced decoded anchors so reversing back into the hero is
        // immediate. Mobile cache warming stays single-lane to avoid contention.
        const anchorStep = 5
        const anchors = []

        for (let index = anchorStep; index < this.urls.length; index += anchorStep) {
          anchors.push(index)
        }
        if (!anchors.includes(this.urls.length - 1)) anchors.push(this.urls.length - 1)
        this.anchorIndexes = new Set([0, ...anchors])

        const anchorSet = new Set(anchors)
        const remaining = []
        for (let offset = 1; offset < anchorStep; offset += 1) {
          for (let index = offset; index < this.urls.length; index += anchorStep) {
            if (!anchorSet.has(index)) remaining.push(index)
          }
        }
        this.mobileAnchorIndexes = anchors
        this.remainingWarmIndexes = remaining
        this.skipCacheWarm = saveData

        // On phones, frame decoding and cache requests must never compete with
        // an active vertical gesture. The end-of-hero scroll handler starts the
        // same work after a quiet period, one item at a time.
        if (this.mobileCropQuery.matches) {
          this.dataset.kravenSequencePrefetch = saveData ? 'save-data' : 'waiting'
          return
        }

        await Promise.all(anchors.map((index) => this.loadFrame(index)))
        if (!this.isConnected || saveData || this.cacheAbortController.signal.aborted) return

        const warmConcurrency = this.mobileCropQuery.matches || isSlowConnection ? 1 : 3
        const continueWarming = () => this.prefetchFrames(remaining, warmConcurrency)

        if ('requestIdleCallback' in window) {
          this.cacheWarmIdle = window.requestIdleCallback(continueWarming, { timeout: 900 })
        } else {
          this.cacheWarmTimer = window.setTimeout(continueWarming, 120)
        }
      }

      async prefetchFrames(indexes, concurrency) {
        let cursor = 0
        const signal = this.cacheAbortController.signal
        const worker = async () => {
          while (cursor < indexes.length && this.isConnected && !signal.aborted) {
            const index = indexes[cursor]
            cursor += 1
            if (this.images.get(index)?.loaded || this.warmedFrameIndexes.has(index)) continue

            try {
              const response = await fetch(this.urls[index], { cache: 'force-cache', signal })
              if (response.ok) {
                await response.blob()
                this.warmedFrameIndexes.add(index)
              }
            } catch (error) {
              if (error?.name !== 'AbortError') this.dataset.kravenSequencePrefetch = 'fallback'
            }
          }
        }

        await Promise.all(Array.from({ length: concurrency }, () => worker()))
        if (!signal.aborted) this.dataset.kravenSequencePrefetch = 'ready'
      }

      pauseMobileCacheWarm() {
        if (!this.mobileCropQuery.matches || this.skipCacheWarm) return

        this.mobileWarmGeneration += 1
        if (!this.cacheAbortController?.signal.aborted) this.cacheAbortController?.abort()
        if (this.dataset.kravenSequencePrefetch !== 'paused') {
          this.dataset.kravenSequencePrefetch = 'paused'
        }

        if (this.mobileWarmResumeTimer) window.clearTimeout(this.mobileWarmResumeTimer)
        this.mobileWarmResumeTimer = window.setTimeout(() => {
          this.mobileWarmResumeTimer = 0
          this.resumeMobileCacheWarm()
        }, 1600)
      }

      async resumeMobileCacheWarm() {
        if (
          !this.isConnected ||
          !this.mobileCropQuery.matches ||
          this.skipCacheWarm ||
          this.targetIndex !== this.urls.length - 1
        ) {
          return
        }

        const generation = this.mobileWarmGeneration
        this.cacheAbortController = new AbortController()
        this.dataset.kravenSequencePrefetch = 'resuming'

        for (const index of this.mobileAnchorIndexes) {
          if (generation !== this.mobileWarmGeneration || !this.isConnected) return
          if (this.images.get(index)?.loaded) continue
          await this.loadFrame(index)
          if (generation !== this.mobileWarmGeneration || !this.isConnected) return
        }

        const pending = this.remainingWarmIndexes.filter(
          (index) => !this.warmedFrameIndexes.has(index) && !this.images.get(index)?.loaded
        )
        if (!pending.length) {
          this.dataset.kravenSequencePrefetch = 'ready'
          return
        }

        await this.prefetchFrames(pending, 1)
      }

      preloadWindow(centerIndex) {
        const firstOffset = this.squeezeQuery.matches ? -4 : -2
        const lastOffset = this.squeezeQuery.matches ? 5 : 4
        for (let offset = firstOffset; offset <= lastOffset; offset += 1) {
          this.loadFrame(centerIndex + offset)
        }
        this.trimDecodedFrames(centerIndex)
      }

      trimDecodedFrames(centerIndex) {
        const decodedLimit = this.squeezeQuery.matches ? 18 : 14
        const keepDistance = this.squeezeQuery.matches ? 7 : 6
        if (this.images.size <= decodedLimit) return

        const removable = [...this.images.keys()]
          .filter(
            (index) =>
              this.images.get(index)?.loaded &&
              Math.abs(index - centerIndex) > keepDistance &&
              index !== 0 &&
              index !== this.urls.length - 1 &&
              !this.anchorIndexes.has(index)
          )
          .sort((first, second) => Math.abs(second - centerIndex) - Math.abs(first - centerIndex))

        while (this.images.size > decodedLimit && removable.length) {
          const index = removable.shift()
          const entry = this.images.get(index)
          if (entry?.image) {
            entry.image.onload = null
            entry.image.onerror = null
            entry.image.src = ''
          }
          entry?.resolve?.(null)
          this.images.delete(index)
        }
      }

      releaseMobileFrames(keepIndex) {
        if (!this.mobileCropQuery.matches || this.targetIndex !== keepIndex) return

        const retainedIndexes = new Set([...this.anchorIndexes, keepIndex])
        this.images.forEach((entry, index) => {
          if (retainedIndexes.has(index) || !entry.loaded) return
          if (entry.image) {
            entry.image.onload = null
            entry.image.onerror = null
            entry.image.src = ''
          }
          entry.resolve?.(null)
          this.images.delete(index)
        })
        this.anchorIndexes = retainedIndexes
        this.mobileFramesReleased = true
        this.dataset.kravenSequenceMemory = 'anchors'
      }

      resize() {
        const pixelRatioLimit =
          this.isIOSWebKit && this.mobileCropQuery.matches ? 1.1 : this.squeezeQuery.matches ? 1.5 : 1.25
        const pixelRatio = Math.min(window.devicePixelRatio || 1, pixelRatioLimit)
        const width = Math.max(Math.round(this.stage.clientWidth * pixelRatio), 1)
        const height = Math.max(Math.round(this.stage.clientHeight * pixelRatio), 1)
        const compositeStageWidth = Math.max(Math.round(this.stage.clientWidth), 1)
        const compositeStageHeight = Math.max(Math.round(this.stage.clientHeight), 1)
        if (this.useIOSAtlas) {
          this.style.setProperty('--kraven-ios-stage-height', `${compositeStageHeight}px`)
          this.style.setProperty(
            '--kraven-ios-root-height',
            `${Math.round(compositeStageHeight * 1.45)}px`
          )
        }

        const previousRenderedIndex = this.renderedIndex
        const dimensionsChanged = this.canvas.width !== width || this.canvas.height !== height

        if (dimensionsChanged) {
          this.canvas.width = width
          this.canvas.height = height
          this.renderedIndex = -1
          this.iosLastDrawnPosition = null
          this.iosCompositeGeometryVersion += 1
          this.iosCompositeStageWidth = compositeStageWidth
          this.iosCompositeStageHeight = compositeStageHeight

          if (this.useIOSAtlas) {
            this.drawIOSAtlasPosition(this.iosVisualPosition)
          } else if (previousRenderedIndex >= 0 && this.images.get(previousRenderedIndex)?.loaded) {
            this.drawFrame(previousRenderedIndex)
          }
        }
        this.schedule()
      }

      schedule() {
        if (!this.useIOSAtlas && !this.isNearViewport && this.targetIndex === this.urls.length - 1) {
          this.pauseMobileCacheWarm()
          return
        }
        if (this.frame) return
        this.frame = requestAnimationFrame(this.update)
      }

      updateIOSStageState(actualScrollTop, sequenceStart, distance) {
        if (!this.useIOSAtlas) return
        const offset = actualScrollTop - sequenceStart
        const nextState = offset < -1 ? 'before' : offset > distance + 1 ? 'after' : 'active'
        if (nextState === this.iosStageState) return
        this.iosStageState = nextState
        this.dataset.kravenStageState = nextState
      }

      update() {
        this.frame = 0
        const actualScrollTop = this.scrollContainer?.scrollTop || 0
        const scrollTop = this.stabilizeIOSScrollTop(actualScrollTop)
        const containerTop =
          this.squeezeQuery.matches && this.scrollContainer instanceof Element
            ? this.scrollContainer.getBoundingClientRect().top
            : 0
        const rootTop = actualScrollTop + this.getBoundingClientRect().top - containerTop
        const distance = Math.max(this.offsetHeight - this.stage.offsetHeight, 1)
        // The transparent header intentionally overlaps the hero and can make
        // its layout rect start above zero. That visual overlap is not scrub
        // progress; the sequence must still open on frame zero.
        const sequenceStart = Math.max(rootTop, 0)
        this.updateIOSStageState(actualScrollTop, sequenceStart, distance)
        const scrollOffset = scrollTop - sequenceStart
        const iosOpeningGuard =
          this.useIOSAtlas &&
          !this.hasTouchScrubStarted &&
          performance.now() < this.iosOpeningGuardUntil &&
          this.isIOSOpeningViewport()
        const guardedOffset = iosOpeningGuard
          ? 0
          : this.mobileCropQuery.matches && !this.hasTouchScrubStarted && scrollOffset <= 24
            ? 0
            : scrollOffset
        const progress = Math.min(Math.max(guardedOffset / distance, 0), 1)
        const rawFramePosition = progress * (this.urls.length - 1)
        const stableFramePosition = rawFramePosition
        if (this.useIOSAtlas) this.iosGestureFramePosition = stableFramePosition
        const nextIndex = Math.round(stableFramePosition)
        const cueOpacity = Math.min(Math.max(1 - progress * 12, 0), 1)

        if (Math.abs(progress - this.iosLastProgress) > 0.00005) {
          const cueStyle = this.cue?.style || this.style
          const moveCue = cueOpacity > 0 || this.lastCueOpacity > 0
          if (cueStyle && cueOpacity !== this.lastCueOpacity) {
            cueStyle.setProperty('--kraven-cue-opacity', cueOpacity)
            cueStyle.setProperty('--kraven-cue-animation', cueOpacity > 0 ? 'running' : 'paused')
            this.lastCueOpacity = cueOpacity
          }
          if (cueStyle && moveCue) {
            cueStyle.setProperty('--kraven-cue-shift', `${progress * 18}px`)
          }
          this.dataset.kravenProgress = progress.toFixed(4)
          this.iosLastProgress = progress
        }
        if (this.dataset.kravenFrame !== String(nextIndex)) this.dataset.kravenFrame = String(nextIndex)
        this.targetIndex = nextIndex
        if (document.body.classList.contains('kraven-cinematic-header-active') !== (progress < 0.999)) {
          document.body.classList.toggle('kraven-cinematic-header-active', progress < 0.999)
        }

        if (this.useIOSAtlas) {
          this.iosDesiredPosition = stableFramePosition
          if (!this.hasTouchScrubStarted || iosOpeningGuard) {
            this.iosVisualPosition = stableFramePosition
          } else {
            const delta = stableFramePosition - this.iosVisualPosition
            if (Math.abs(delta) <= 0.025) {
              this.iosVisualPosition = stableFramePosition
            } else {
              const step = Math.min(Math.max(Math.abs(delta) * 0.64, 0.08), 2.4)
              this.iosVisualPosition += Math.sign(delta) * Math.min(step, Math.abs(delta))
            }
          }
          this.preloadIOSAtlasWindow(this.iosVisualPosition)
          this.drawIOSAtlasPosition(this.iosVisualPosition)
        } else {
          const entry = this.images.get(nextIndex)
          if (entry?.loaded) {
            if (this.renderedIndex !== nextIndex) this.drawFrame(nextIndex)
          } else {
            this.drawNearestFrame(nextIndex)
            this.loadFrame(nextIndex, true)
          }
        }

        if (this.useIOSAtlas) {
          this.iosLastSampledScrollTop = actualScrollTop
          const needsCatchup = Math.abs(this.iosDesiredPosition - this.iosVisualPosition) > 0.025
          if (
            !this.frame &&
            (this.isIOSTouching || performance.now() < this.iosMomentumUntil || needsCatchup)
          ) {
            this.frame = requestAnimationFrame(this.update)
          }
          return
        }

        if (this.mobileCropQuery.matches && progress >= 0.999) {
          this.pauseMobileCacheWarm()
          if (!this.mobileFramesReleased && !this.mobileReleaseTimer) {
            this.mobileReleaseTimer = window.setTimeout(() => {
              this.mobileReleaseTimer = 0
              this.releaseMobileFrames(nextIndex)
            }, 180)
          }
        } else {
          if (this.mobileWarmResumeTimer) {
            window.clearTimeout(this.mobileWarmResumeTimer)
            this.mobileWarmResumeTimer = 0
          }
          if (this.mobileReleaseTimer) {
            window.clearTimeout(this.mobileReleaseTimer)
            this.mobileReleaseTimer = 0
          }
          if (this.mobileFramesReleased) {
            this.mobileFramesReleased = false
            this.dataset.kravenSequenceMemory = 'active'
          }
          this.preloadWindow(nextIndex)
        }
      }

      drawIOSAtlasPosition(framePosition) {
        if (!this.useIOSAtlas || this.iosCompositeLayers.length < 2) return
        const boundedPosition = Math.min(Math.max(framePosition, 0), this.frameCount - 1)
        if (
          Number.isFinite(this.iosLastDrawnPosition) &&
          Math.abs(boundedPosition - this.iosLastDrawnPosition) < 0.002
        ) {
          return
        }
        const firstIndex = Math.floor(boundedPosition)
        const secondIndex = Math.min(firstIndex + 1, this.frameCount - 1)
        const blend = boundedPosition - firstIndex
        const firstAtlas = this.iosAtlases.get(Math.floor(firstIndex / this.iosAtlasSize))
        const secondAtlas = this.iosAtlases.get(Math.floor(secondIndex / this.iosAtlasSize))

        if (!firstAtlas?.loaded) return

        this.positionIOSCompositeLayer(this.iosCompositeLayers[0], firstIndex, firstAtlas, 1)
        const canBlend = blend > 0.015 && secondIndex !== firstIndex && secondAtlas?.loaded
        if (canBlend) {
          this.positionIOSCompositeLayer(this.iosCompositeLayers[1], secondIndex, secondAtlas, blend)
        } else {
          this.iosCompositeLayers[1].layer.style.opacity = '0'
        }

        const displayedIndex = Math.round(boundedPosition)
        this.renderedIndex = displayedIndex
        this.iosLastDrawnPosition = boundedPosition
        this.dataset.kravenCompositeReady = 'true'
        this.dataset.kravenRenderedFrame = String(displayedIndex)
        this.dataset.kravenSequenceState = 'ready'
      }

      positionIOSCompositeLayer(slot, frameIndex, atlasEntry, opacity) {
        if (!slot || !atlasEntry?.loaded || !atlasEntry.image?.naturalWidth) return
        const atlasIndex = Math.floor(frameIndex / this.iosAtlasSize)
        const localIndex = frameIndex % this.iosAtlasSize
        const column = localIndex % this.iosAtlasColumns
        const row = Math.floor(localIndex / this.iosAtlasColumns)
        const naturalTileWidth = atlasEntry.image.naturalWidth / this.iosAtlasColumns
        const naturalTileHeight = atlasEntry.image.naturalHeight / this.iosAtlasRows
        // Use the geometry captured by resize(). iOS toolbar animation can
        // otherwise make clientHeight alternate by a pixel while the finger
        // is down, visibly shifting the crop between adjacent frames.
        const stageWidth = this.iosCompositeStageWidth || Math.max(this.stage.clientWidth, 1)
        const stageHeight = this.iosCompositeStageHeight || Math.max(this.stage.clientHeight, 1)
        const scale = Math.max(stageWidth / naturalTileWidth, stageHeight / naturalTileHeight)
        const tileWidth = naturalTileWidth * scale
        const tileHeight = naturalTileHeight * scale
        const cropX = Math.max((tileWidth - stageWidth) / 2, 0)
        const cropY = Math.max((tileHeight - stageHeight) / 2, 0)

        if (
          slot.frameIndex !== frameIndex ||
          slot.geometryVersion !== this.iosCompositeGeometryVersion
        ) {
          const source = this.iosAtlasUrls[atlasIndex]
          if (slot.image.getAttribute('src') !== source) slot.image.src = source
          slot.image.style.width = `${tileWidth * this.iosAtlasColumns}px`
          slot.image.style.height = `${tileHeight * this.iosAtlasRows}px`
          slot.image.style.transform = `translate3d(${-column * tileWidth - cropX}px, ${
            -row * tileHeight - cropY
          }px, 0)`
          slot.frameIndex = frameIndex
          slot.geometryVersion = this.iosCompositeGeometryVersion
        }
        slot.layer.style.opacity = String(opacity)
      }

      drawIOSAtlasTile(frameIndex, atlasImage, opacity) {
        if (!atlasImage?.naturalWidth || !atlasImage?.naturalHeight) return
        const localIndex = frameIndex % this.iosAtlasSize
        const column = localIndex % this.iosAtlasColumns
        const row = Math.floor(localIndex / this.iosAtlasColumns)
        const tileWidth = atlasImage.naturalWidth / this.iosAtlasColumns
        const tileHeight = atlasImage.naturalHeight / this.iosAtlasRows
        const canvasWidth = this.canvas.width
        const canvasHeight = this.canvas.height
        const scale = Math.max(canvasWidth / tileWidth, canvasHeight / tileHeight)
        const sourceWidth = canvasWidth / scale
        const sourceHeight = canvasHeight / scale
        const tileX = column * tileWidth
        const tileY = row * tileHeight
        const sourceX = tileX + Math.max((tileWidth - sourceWidth) / 2, 0)
        const sourceY = tileY + Math.max((tileHeight - sourceHeight) / 2, 0)

        this.context.globalAlpha = opacity
        this.context.drawImage(
          atlasImage,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          canvasWidth,
          canvasHeight
        )
      }

      drawNearestIOSAtlasFrame(targetIndex) {
        const loadedAtlasIndexes = [...this.iosAtlases.entries()]
          .filter(([, entry]) => entry.loaded)
          .map(([index]) => index)
        if (!loadedAtlasIndexes.length) return

        let nearestFrame = 0
        let nearestDistance = Number.POSITIVE_INFINITY
        loadedAtlasIndexes.forEach((atlasIndex) => {
          const first = atlasIndex * this.iosAtlasSize
          const last = Math.min(first + this.iosAtlasSize - 1, this.frameCount - 1)
          const candidate = Math.min(Math.max(targetIndex, first), last)
          const distance = Math.abs(candidate - targetIndex)
          if (distance < nearestDistance) {
            nearestDistance = distance
            nearestFrame = candidate
          }
        })

        const entry = this.iosAtlases.get(Math.floor(nearestFrame / this.iosAtlasSize))
        if (!entry?.loaded) return
        this.context.save()
        this.context.globalAlpha = 1
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.drawIOSAtlasTile(nearestFrame, entry.image, 1)
        this.context.restore()
        this.renderedIndex = nearestFrame
        this.iosLastDrawnPosition = null
        this.dataset.kravenRenderedFrame = String(nearestFrame)
        this.dataset.kravenSequenceState = 'ready'
      }

      drawNearestFrame(targetIndex) {
        const loadedIndexes = [...this.images.entries()]
          .filter(([, entry]) => entry.loaded)
          .map(([index]) => index)
          .sort((first, second) => Math.abs(first - targetIndex) - Math.abs(second - targetIndex))

        if (!loadedIndexes.length) return

        const nearestIndex = loadedIndexes[0]
        const nearestDistance = Math.abs(nearestIndex - targetIndex)
        const renderedDistance =
          this.renderedIndex < 0 ? Number.POSITIVE_INFINITY : Math.abs(this.renderedIndex - targetIndex)

        if (nearestDistance < renderedDistance) this.drawFrame(nearestIndex)
      }

      drawFrame(index) {
        const entry = this.images.get(index)
        if (!entry?.loaded || !entry.image.naturalWidth || !entry.image.naturalHeight) return

        const canvasWidth = this.canvas.width
        const canvasHeight = this.canvas.height
        const imageWidth = entry.image.naturalWidth
        const imageHeight = entry.image.naturalHeight
        const scale = Math.max(canvasWidth / imageWidth, canvasHeight / imageHeight)
        const sourceWidth = canvasWidth / scale
        const sourceHeight = canvasHeight / scale
        const focalX = imageWidth / 2
        const sourceX = Math.min(
          Math.max(focalX - sourceWidth / 2, 0),
          Math.max(imageWidth - sourceWidth, 0)
        )
        const sourceY = (imageHeight - sourceHeight) / 2

        this.context.clearRect(0, 0, canvasWidth, canvasHeight)
        this.context.drawImage(
          entry.image,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          canvasWidth,
          canvasHeight
        )
        this.renderedIndex = index
        this.dataset.kravenRenderedFrame = String(index)
        this.dataset.kravenSequenceState = 'ready'
      }
    }
  )
}
